import apiClient, { getAccessToken } from "./api";

function normalizeCurrency(value, fallback = "EUR") {
  const normalized = String(value || fallback)
    .trim()
    .toUpperCase();
  return /^[A-Z]{3}$/.test(normalized) ? normalized : fallback;
}

function normalizeSymbols(symbols = []) {
  return Array.from(
    new Set(
      symbols
        .map((symbol) =>
          String(symbol || "")
            .trim()
            .toUpperCase(),
        )
        .filter((symbol) => /^[A-Z]{3}$/.test(symbol)),
    ),
  );
}

function normalizeError(error, fallbackMessage) {
  const status = Number(error?.response?.status || 0);
  const message =
    error?.response?.data?.message || error?.message || fallbackMessage;
  return {
    status,
    message,
    source: error?.response?.data?.source || null,
    stats: error?.response?.data?.stats || null,
  };
}

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

function safeValue(value, fallback = null) {
  if (value === undefined || value === null || Number.isNaN(value))
    return fallback;
  return value;
}

function normalizeSyncStateRow(row) {
  return {
    source: String(row?.source || "Unknown"),
    baseCurrency: normalizeCurrency(row?.baseCurrency || "EUR"),
    lastSuccessfulSyncAt: safeValue(row?.lastSuccessfulSyncAt, null),
    lastAttemptAt: safeValue(row?.lastAttemptAt, null),
    consecutiveFailures: Number(row?.consecutiveFailures || 0),
    lastError: safeValue(row?.lastError, null),
    updatedAt: safeValue(row?.updatedAt, null),
  };
}

function normalizeAutoSyncStatus(response) {
  const data = response?.data?.data || response?.data || {};
  return {
    enabled: Boolean(data?.enabled),
    running: Boolean(data?.running),
    allBaseCurrencies: Boolean(data?.allBaseCurrencies),
    intervalHours: Number(data?.intervalHours || 0),
    baseCurrency: normalizeCurrency(data?.baseCurrency || "EUR"),
    startedAt: safeValue(data?.startedAt, null),
    nextRunAt: safeValue(data?.nextRunAt, null),
    lastRunStartedAt: safeValue(data?.lastRunStartedAt, null),
    lastRunFinishedAt: safeValue(data?.lastRunFinishedAt, null),
    lastRunStatus: safeValue(data?.lastRunStatus, null),
    lastRunReason: safeValue(data?.lastRunReason, null),
    lastErrorMessage: safeValue(data?.lastErrorMessage, null),
  };
}

function normalizeOverview(response) {
  const summary =
    response?.data?.data?.summary || response?.data?.summary || {};
  return {
    summary: {
      lastSuccessfulSyncAt: safeValue(summary?.lastSuccessfulSyncAt, null),
      lastAttemptAt: safeValue(summary?.lastAttemptAt, null),
      freshPairs: Number(summary?.freshPairs || 0),
      stalePairs: Number(summary?.stalePairs || 0),
      missingPairs: Number(summary?.missingPairs || 0),
      failedProviders: Number(summary?.failedProviders || 0),
      totalPairs: Number(summary?.totalPairs || 0),
      mostRecentRateDate: safeValue(summary?.mostRecentRateDate, null),
      oldestRateDate: safeValue(summary?.oldestRateDate, null),
      defaultBaseCurrency: normalizeCurrency(
        summary?.defaultBaseCurrency || "EUR",
      ),
      dashboardConversionHealth: String(
        summary?.dashboardConversionHealth || "Unknown",
      ),
    },
  };
}

function normalizeMatrixCell(cell) {
  return {
    baseCurrency: normalizeCurrency(cell?.baseCurrency || ""),
    quoteCurrency: normalizeCurrency(cell?.quoteCurrency || ""),
    rateDecimal:
      cell?.rateDecimal === null || cell?.rateDecimal === undefined
        ? null
        : String(cell.rateDecimal),
    rateDate: safeValue(cell?.rateDate, null),
    source: safeValue(cell?.source, null),
    sourcePriority:
      cell?.sourcePriority === null || cell?.sourcePriority === undefined
        ? null
        : Number(cell.sourcePriority),
    fetchedAt: safeValue(cell?.fetchedAt, null),
    status: String(cell?.status || "missing"),
    freshness: String(cell?.freshness || "missing"),
    isDerived: Boolean(cell?.isDerived),
    derivedVia: safeValue(cell?.derivedVia, null),
    validFrom: safeValue(cell?.validFrom, null),
    validTo: safeValue(cell?.validTo, null),
  };
}

function normalizeMatrix(response) {
  const data = response?.data?.data || response?.data || {};
  return {
    asOfDate: safeValue(data?.asOfDate, null),
    page: Number(data?.page || 1),
    pageSize: Number(data?.pageSize || 12),
    total: Number(data?.total || 0),
    totalPages: Number(data?.totalPages || 1),
    sort: String(data?.sort || "baseCurrency:asc"),
    baseCurrencies: toArray(data?.baseCurrencies)
      .map((item) => normalizeCurrency(item))
      .filter(Boolean),
    quoteCurrencies: toArray(data?.quoteCurrencies)
      .map((item) => normalizeCurrency(item))
      .filter(Boolean),
    cells: toArray(data?.cells).map(normalizeMatrixCell),
    summary: {
      totalCells: Number(data?.summary?.totalCells || 0),
      freshCount: Number(data?.summary?.freshCount || 0),
      staleCount: Number(data?.summary?.staleCount || 0),
      missingCount: Number(data?.summary?.missingCount || 0),
      derivedCount: Number(data?.summary?.derivedCount || 0),
    },
  };
}

function normalizePairs(response) {
  const data = response?.data?.data || response?.data || {};
  const rows = toArray(data?.rows).map(normalizeMatrixCell);
  return {
    page: Number(data?.page || 1),
    pageSize: Number(data?.pageSize || rows.length || 1),
    total: Number(data?.total || rows.length),
    totalPages: Number(data?.totalPages || 1),
    sort: String(data?.sort || "rateDate:desc"),
    rows,
  };
}

function normalizeSyncResponse(response) {
  const data = response?.data || {};
  const syncRows = toArray(data?.syncState || data?.data).map(
    normalizeSyncStateRow,
  );
  return {
    ok: Boolean(data?.ok ?? data?.success ?? true),
    message: String(data?.message || "Exchange rates synced"),
    stats: {
      fetched: Number(data?.stats?.fetched || 0),
      created: Number(data?.stats?.created || 0),
      updated: Number(data?.stats?.updated || 0),
      skipped: Number(data?.stats?.skipped || 0),
      failed: Number(data?.stats?.failed || 0),
      durationMs: Number(data?.stats?.durationMs || 0),
    },
    syncState: syncRows,
  };
}

export async function getExchangeRateOverview(params = {}) {
  try {
    const response = await apiClient.get("/admin/exchange-rates/overview", {
      params,
    });
    return normalizeOverview(response);
  } catch (error) {
    throw normalizeError(error, "Unable to load exchange-rate overview");
  }
}

export async function getExchangeRateMatrix(params = {}) {
  const payload = {
    ...params,
    baseCurrencies: normalizeSymbols(
      Array.isArray(params.baseCurrencies) ? params.baseCurrencies : [],
    ),
    quoteCurrencies: normalizeSymbols(
      Array.isArray(params.quoteCurrencies) ? params.quoteCurrencies : [],
    ),
  };

  Object.keys(payload).forEach((key) => {
    if (
      payload[key] === undefined ||
      payload[key] === null ||
      payload[key] === ""
    ) {
      delete payload[key];
    }
  });

  try {
    const response = await apiClient.get("/admin/exchange-rates/matrix", {
      params: payload,
    });
    return normalizeMatrix(response);
  } catch (error) {
    throw normalizeError(error, "Unable to load exchange-rate matrix");
  }
}

export async function getExchangeRatePairs(params = {}) {
  const payload = {
    ...params,
    baseCurrency: params.baseCurrency
      ? normalizeCurrency(params.baseCurrency)
      : undefined,
    quoteCurrency: params.quoteCurrency
      ? normalizeCurrency(params.quoteCurrency)
      : undefined,
  };

  Object.keys(payload).forEach((key) => {
    if (
      payload[key] === undefined ||
      payload[key] === null ||
      payload[key] === ""
    ) {
      delete payload[key];
    }
  });

  try {
    const response = await apiClient.get("/admin/exchange-rates/pairs", {
      params: payload,
    });
    return normalizePairs(response);
  } catch (error) {
    throw normalizeError(error, "Unable to load exchange-rate pairs");
  }
}

export async function getExchangeRateSyncStatus(sourceOrParams) {
  const source =
    typeof sourceOrParams === "string"
      ? sourceOrParams
      : sourceOrParams?.source;
  const params =
    typeof source === "string" && source.trim()
      ? { source: source.trim() }
      : undefined;

  try {
    const response = await apiClient.get(
      "/admin/exchange-rates/sync-status",
      params ? { params } : undefined,
    );
    return toArray(response?.data?.data).map(normalizeSyncStateRow);
  } catch (error) {
    const status = Number(error?.response?.status || 0);
    if (status === 404) {
      try {
        const fallbackResponse = await apiClient.get(
          "/admin/exchange-rates/status",
          params ? { params } : undefined,
        );
        return toArray(fallbackResponse?.data?.data).map(normalizeSyncStateRow);
      } catch (fallbackError) {
        throw normalizeError(
          fallbackError,
          "Unable to load exchange-rate sync status",
        );
      }
    }
    throw normalizeError(error, "Unable to load exchange-rate sync status");
  }
}

function normalizeSyncStreamEvent(data = {}) {
  const stats = data?.stats || {};
  return {
    type: String(data?.type || "progress"),
    ok: data?.ok === undefined ? true : Boolean(data.ok),
    baseCurrency: data?.baseCurrency
      ? normalizeCurrency(data.baseCurrency)
      : null,
    processed: Number(data?.processed || 0),
    total: Number(data?.total || 0),
    remaining: Number(data?.remaining || 0),
    syncedCount: Number(data?.syncedCount || stats?.fetched || 0),
    missingCount: Number(data?.missingCount || 0),
    failedCount: Number(data?.failedCount || stats?.failed || 0),
    durationMs: Number(data?.durationMs || stats?.durationMs || 0),
    message: String(data?.message || ""),
    stats: {
      fetched: Number(stats?.fetched || data?.syncedCount || 0),
      created: Number(stats?.created || 0),
      updated: Number(stats?.updated || data?.syncedCount || 0),
      skipped: Number(stats?.skipped || 0),
      failed: Number(stats?.failed || data?.failedCount || 0),
      durationMs: Number(stats?.durationMs || data?.durationMs || 0),
    },
    syncState: toArray(data?.syncState).map(normalizeSyncStateRow),
  };
}

export async function getExchangeRateAutoSyncStatus() {
  try {
    const response = await apiClient.get(
      "/admin/exchange-rates/auto-sync/status",
    );
    return normalizeAutoSyncStatus(response);
  } catch (error) {
    throw normalizeError(
      error,
      "Unable to load exchange-rate auto-sync status",
    );
  }
}

export async function syncExchangeRates(options = {}) {
  const payload = {
    baseCurrency: normalizeCurrency(options.baseCurrency || "EUR"),
  };
  if (options.allBaseCurrencies === true) {
    payload.allBaseCurrencies = true;
  }

  const symbols = normalizeSymbols(
    Array.isArray(options.symbols) ? options.symbols : [],
  );
  if (symbols.length > 0) {
    payload.symbols = symbols;
  }

  if (
    typeof options.fromDate === "string" &&
    typeof options.toDate === "string"
  ) {
    payload.fromDate = options.fromDate.trim();
    payload.toDate = options.toDate.trim();
  }

  try {
    const response = await apiClient.post(
      "/admin/exchange-rates/sync",
      payload,
    );
    return normalizeSyncResponse(response);
  } catch (error) {
    throw normalizeError(error, "Unable to sync exchange rates");
  }
}

export function syncExchangeRatesStream(options = {}, handlers = {}) {
  const token = String(getAccessToken() || "").trim();
  if (!token) {
    return Promise.reject({
      status: 401,
      message: "Missing authorization token",
    });
  }
  if (
    typeof window === "undefined" ||
    typeof window.EventSource !== "function"
  ) {
    return Promise.reject({
      status: 0,
      message: "Streaming sync is not supported in this browser",
    });
  }

  const base = apiClient.defaults.baseURL || "/api";
  const url = new URL(
    `${base.replace(/\/$/, "")}/admin/exchange-rates/sync/stream`,
    window.location.origin,
  );
  url.searchParams.set("access_token", token);
  const symbols = normalizeSymbols(
    Array.isArray(options.symbols) ? options.symbols : [],
  );
  if (symbols.length > 0) {
    url.searchParams.set("symbols", symbols.join(","));
  }

  return new Promise((resolve, reject) => {
    const source = new window.EventSource(url.toString(), {
      withCredentials: true,
    });
    let settled = false;
    let latest = null;

    const close = () => {
      if (typeof source.close === "function") source.close();
    };

    source.onmessage = (event) => {
      try {
        const parsed = normalizeSyncStreamEvent(JSON.parse(event.data));
        latest = parsed;
        handlers.onProgress?.(parsed);
        if (parsed.type === "done") {
          settled = true;
          close();
          resolve({
            ok: parsed.ok,
            message: parsed.message || "Full exchange-rate sync finished",
            stats: parsed.stats,
            syncState: parsed.syncState,
            progress: parsed,
          });
        }
        if (parsed.type === "error") {
          settled = true;
          close();
          reject({
            status: 0,
            message: parsed.message || "Full exchange-rate sync failed",
            progress: parsed,
          });
        }
      } catch {
        // Ignore malformed stream chunks; the server keeps sending progress.
      }
    };

    source.onerror = () => {
      if (settled) return;
      settled = true;
      close();
      reject({
        status: 0,
        message:
          latest?.message || "Full exchange-rate sync stream disconnected",
        progress: latest,
      });
    };
  });
}

export async function runExchangeRateSync(options = {}) {
  const normalized = await syncExchangeRates(options);
  return normalized.syncState;
}

const exchangeRateService = {
  getExchangeRateOverview,
  getExchangeRateMatrix,
  getExchangeRatePairs,
  getExchangeRateSyncStatus,
  getExchangeRateAutoSyncStatus,
  syncExchangeRates,
  syncExchangeRatesStream,
  runExchangeRateSync,
};

export default exchangeRateService;
